import { ArrowRight, LockKeyhole } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Logo } from './Logo';
import {
  emailAuthError,
  requestEmailCode,
  verifyEmailCode,
} from '../lib/email-auth';
import { isConfigured, supabase } from '../lib/supabase';

export function Login({ initialError }: { initialError?: string | null }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [message, setMessage] = useState<string | null>(initialError ?? null);
  const [isError, setIsError] = useState(Boolean(initialError));
  const [busy, setBusy] = useState(false);

  async function sendCode() {
    if (!supabase) return;
    setBusy(true);
    setMessage(null);
    try {
      const { error } = await requestEmailCode(supabase.auth, email);
      if (error) throw error;
      setCode('');
      setCodeSent(true);
      setIsError(false);
      setMessage(
        'Enviamos um código de acesso para o seu e-mail. Digite-o abaixo.',
      );
    } catch (cause) {
      setIsError(true);
      setMessage(
        cause && typeof cause === 'object' && 'code' in cause
          ? emailAuthError(cause as { code?: string; message?: string })
          : 'Não foi possível enviar o código. Confira a conexão e tente novamente.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!supabase) return;
    if (!codeSent) return sendCode();
    setBusy(true);
    setMessage(null);
    try {
      const { data, error } = await verifyEmailCode(supabase.auth, email, code);
      if (error) throw error;
      if (!data.session) throw new Error('Sessão não criada');
      setIsError(false);
      setMessage('Código confirmado. Validando sua sessão…');
    } catch (cause) {
      setIsError(true);
      setMessage(
        cause && typeof cause === 'object' && 'code' in cause
          ? emailAuthError(cause as { code?: string; message?: string })
          : 'Não foi possível confirmar o código. Confira a conexão e tente novamente.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-copy">
        <Logo />
        <div>
          <p className="eyebrow">CONTROL PLANE PESSOAL</p>
          <h1>
            Seu conteúdo.
            <br />
            <em>No seu ritmo.</em>
          </h1>
          <p className="lede">
            Planejamento, controle e automação responsável — em uma base segura
            que trabalha com você.
          </p>
        </div>
        <p className="footnote">Missão 1 · Fundação</p>
      </section>
      <section className="login-panel">
        <form className="login-card" onSubmit={(event) => void submit(event)}>
          <span className="lock">
            <LockKeyhole size={22} />
          </span>
          <p className="eyebrow">ACESSO SEGURO</p>
          <h2>{codeSent ? 'Confirme seu acesso' : 'Bem-vindo'}</h2>
          {!isConfigured ? (
            <div className="notice">
              <strong>Configuração necessária</strong>
              <span>
                Defina as variáveis VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY.
                Nenhum dado de demonstração foi criado.
              </span>
            </div>
          ) : (
            <>
              <label htmlFor="email">E-mail</label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder="voce@exemplo.com"
                value={email}
                readOnly={codeSent}
                onChange={(event) => setEmail(event.target.value)}
              />
              {codeSent && (
                <>
                  <label htmlFor="login-code" className="login-code-label">
                    Código recebido
                  </label>
                  <input
                    id="login-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6,8}"
                    minLength={6}
                    maxLength={8}
                    required
                    placeholder="Digite o código"
                    value={code}
                    onChange={(event) =>
                      setCode(event.target.value.replace(/\D/g, ''))
                    }
                  />
                </>
              )}
              <button className="primary" disabled={busy}>
                {busy ? (
                  'Aguarde…'
                ) : (
                  <>
                    {codeSent ? 'Confirmar código' : 'Receber código de acesso'}{' '}
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
              {codeSent && (
                <div className="login-code-actions">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void sendCode()}
                  >
                    Reenviar código
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setCodeSent(false);
                      setCode('');
                      setMessage(null);
                    }}
                  >
                    Trocar e-mail
                  </button>
                </div>
              )}
              {message && (
                <p
                  className={isError ? 'form-message error' : 'form-message'}
                  role={isError ? 'alert' : 'status'}
                >
                  {message}
                </p>
              )}
            </>
          )}
          <p className="privacy">
            Sem senha de redes sociais. Acesso por código de uso único, sem link
            no e-mail.
          </p>
        </form>
      </section>
    </main>
  );
}
